/// <reference types="vite/client" />
import {
  ColorModeProvider,
  ColorModeScript,
  localStorageManager,
} from "@kobalte/core/color-mode";
import { QueryClientProvider } from "@tanstack/solid-query";
import {
  createRootRouteWithContext,
  ErrorComponent,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/solid-router";
import { lazy, Suspense } from "solid-js";
import { HydrationScript } from "solid-js/web";
import NavigationProgress from "~/components/navigation-progress";
import { Toaster } from "~/components/ui/sonner";
import { getContext } from "~/integrations/tanstack-query/provider";
import "~/styles/app.css";
import { seo } from "~/utils/seo";

// Devtools are opt-in: off by default, enabled explicitly with DEVTOOLS=true
// (exposed to the client as VITE_DEVTOOLS). Both the Vite plugin and these
// runtime components are gated on the same flag.
const devtoolsEnabled =
  import.meta.env.DEV && import.meta.env.VITE_DEVTOOLS === "true";

const SolidQueryDevtools = devtoolsEnabled
  ? lazy(() =>
      import("@tanstack/solid-query-devtools").then((m) => ({
        default: m.SolidQueryDevtools,
      })),
    )
  : () => null;

const TanStackRouterDevtools = devtoolsEnabled
  ? lazy(() =>
      import("@tanstack/solid-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

export const Route = createRootRouteWithContext<
  ReturnType<typeof getContext>
>()({
  head: () => ({
    meta: [
      {
        charset: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, maximum-scale=5",
      },
      ...seo({
        title:
          "Accounting Platform for Teams, Journal Entries, and AI | ActShark",
        description:
          "Accounting platform for modern teams. Manage businesses, accounts, journal entries, documents, worksheets, fixed assets, and AI workflows in one place.",
        path: "/",
        image: "/dashboard-lg.webp?v=5",
      }),
      {
        name: "theme-color",
        content: "#ffffff",
      },
      {
        name: "mobile-web-app-capable",
        content: "yes",
      },
      {
        name: "apple-mobile-web-app-title",
        content: "ActShark",
      },
      {
        name: "apple-mobile-web-app-status-bar-style",
        content: "black-translucent",
      },
    ],
    // Opt authenticated routes out of Cloudflare's blanket speculation rules.
    // Cloudflare injects `{ prefetch: [{ eagerness: "conservative", where: { href_matches: "/*" } }] }`
    // which causes the browser to natively prefetch every sidebar link — including protected
    // dashboard routes — before auth cookies are properly attached, producing cached 503s.
    // TanStack Router's own `defaultPreload: "intent"` handles prefetching correctly instead.
    // scripts: [
    //   {
    //     type: "speculationrules",
    //     children: JSON.stringify({
    //       prefetch: [
    //         {
    //           eagerness: "conservative",
    //           source: "document",
    //           where: {
    //             and: [
    //               { href_matches: "/*" },
    //               { not: { href_matches: "/dashboard/*" } },
    //             ],
    //           },
    //         },
    //       ],
    //     }),
    //   },
    // ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "ActShark",
          url: "https://actshark.com",
          logo: "https://actshark.com/logo.svg",
          description:
            "Cloud and local accounting software with complete data control and zero vendor lock-in",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "Customer Service",
            url: "https://actshark.com",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "ActShark Accounting Software",
          description:
            "Smart accounting software for local and cloud deployment with AI-powered data extraction and complete data control",
          url: "https://actshark.com",
          applicationCategory: "BusinessApplication",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          operatingSystem: "Web",
        }),
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://actshark.com/",
      },
      {
        rel: "alternate",
        hreflang: "en",
        href: "https://actshark.com/",
      },
      {
        rel: "alternate",
        hreflang: "x-default",
        href: "https://actshark.com/",
      },
      {
        rel: "manifest",
        href: "/site.webmanifest",
        fetchpriority: "low",
      },
      // Warm the connection to Cloudflare's analytics beacon origin so the
      // browser can connect early and avoid DNS/TCP/TLS latency later.
      {
        rel: "preconnect",
        href: "https://static.cloudflareinsights.com",
      },
      // Hero LCP image preloads live in the landing page route's head
      // (_main/index.tsx) so they don't fire on every dashboard page.
      // Google Fonts links removed — Cloudflare Fonts rewrites Inter to cf-fonts automatically.
      // Preload the Inter font so it loads in parallel with the CSS, removing
      // it from the serial CSS→font critical path chain.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        crossorigin: "anonymous",
        href: "/fonts/inter-latin-400-normal.woff2",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "96x96",
        href: "/favicon-96x96.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png",
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/favicon.svg",
      },
    ],
  }),
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  shellComponent: RootComponent,
});

function RootComponent() {
  const {
    options: { context },
  } = useRouter();

  return (
    <QueryClientProvider client={context.queryClient}>
      <html lang="en">
        <head>
          <HeadContent />
          <HydrationScript />
          <ColorModeScript storageType={localStorageManager.type} />
        </head>
        <body>
          <NavigationProgress />
          <ColorModeProvider storageManager={localStorageManager}>
            <Suspense>
              <Outlet />
            </Suspense>
            <Toaster />
            <SolidQueryDevtools buttonPosition="bottom-right" />
            <TanStackRouterDevtools position="bottom-right" />
          </ColorModeProvider>
          <Scripts />
        </body>
      </html>
    </QueryClientProvider>
  );
}
